// ============================================================
// FOOD COMPLIANCE PAGE
// ============================================================

import React, { useState, useEffect } from 'react';
import { ShieldCheck, AlertTriangle, Download } from 'lucide-react';
import { Table, Badge, Button, SearchInput, Pagination, Modal, StatCard, Alert } from '../components/ui';
import { usePagination, useSearch } from '../hooks';
import { getFoodCompliance } from '../services/mockData';
import { adminApi } from '../services/apiService';
import { formatDate, downloadCSV } from '../utils/helpers';
import { toast } from 'react-toastify';

const FoodCompliance = () => {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    adminApi.getFoodCompliance(getFoodCompliance)
      .then(res => { setAll(Array.isArray(res) ? res : (res?.items || [])); setLoading(false); })
      .catch(() => { setAll(getFoodCompliance()); setLoading(false); });
  }, []);

  const { query, setQuery, filtered } = useSearch(all, ['restaurant', 'licenseNumber']);
  const { currentPage, totalPages, paginated, goTo } = usePagination(filtered);

  const isExpiringSoon = (date) => {
    const days = (new Date(date) - new Date()) / (1000 * 60 * 60 * 24);
    return days > 0 && days <= 30;
  };

  const columns = [
    { key: 'id', label: 'ID', render: v => <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{v}</span> },
    { key: 'restaurant', label: 'Restaurant', render: v => <b>{v}</b> },
    { key: 'licenseNumber', label: 'FSSAI License', render: v => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span> },
    { key: 'licenseType', label: 'Type', render: v => <span className="admin-tag">{v}</span> },
    { key: 'issuedDate', label: 'Issued', render: v => formatDate(v) },
    {
      key: 'expiryDate', label: 'Expiry',
      render: v => (
        <span style={{
          color: new Date(v) < new Date() ? '#EF4444' : isExpiringSoon(v) ? '#F59E0B' : 'var(--admin-text)',
          fontWeight: 600,
        }}>
          {formatDate(v)}
          {new Date(v) < new Date() && ' 🔴'}
          {isExpiringSoon(v) && ' ⚠️'}
        </span>
      ),
    },
    { key: 'status', label: 'Status', render: v => <Badge status={v} /> },
    { key: 'verifiedBy', label: 'Verified By', render: v => v || '—' },
    {
      key: 'id', label: 'Actions',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button variant="info" size="sm" onClick={() => setSelected(row)}>View</Button>
          {!row.verifiedBy && <Button variant="success" size="sm" onClick={async () => {
            try { await adminApi.verifyCompliance(row.id); } catch { /* fallback */ }
            setAll(p => p.map(x => x.id === row.id ? { ...x, verifiedBy: 'Admin', status: 'active' } : x));
            toast.success('Compliance verified!');
          }}>Verify</Button>}
        </div>
      ),
    },
  ];

  const expiredCount = all.filter(c => c.status === 'expired').length;
  const expiringSoon = all.filter(c => isExpiringSoon(c.expiryDate)).length;

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Food Compliance</h1>
          <p className="admin-page-subtitle">FSSAI license management and tracking</p>
        </div>
        <Button variant="secondary" icon={<Download size={14} />} onClick={() => downloadCSV(all, 'compliance')}>Export</Button>
      </div>

      {(expiredCount > 0 || expiringSoon > 0) && (
        <Alert type="warning">
          <AlertTriangle size={16} />
          <span><b>{expiredCount} expired</b> and <b>{expiringSoon} expiring soon</b> FSSAI licenses require immediate attention!</span>
        </Alert>
      )}

      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 20 }}>
        <StatCard label="Total" value={all.length} icon={<ShieldCheck size={18} />} color="#3B82F6" />
        <StatCard label="Active" value={all.filter(c => c.status === 'active').length} icon={<ShieldCheck size={18} />} color="#10B981" />
        <StatCard label="Expiring Soon" value={expiringSoon} icon={<AlertTriangle size={18} />} color="#F59E0B" />
        <StatCard label="Expired" value={expiredCount} icon={<AlertTriangle size={18} />} color="#EF4444" />
      </div>

      <div className="admin-table-wrapper">
        <div className="admin-table-toolbar">
          <SearchInput value={query} onChange={setQuery} placeholder="Search licenses..." />
        </div>
        <Table columns={columns} data={paginated} loading={loading} />
        <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={filtered.length} itemsPerPage={10} onPageChange={goTo} />
      </div>

      <Modal open={!!selected} onClose={() => setSelected(null)} title="FSSAI Compliance Details" size="md"
        footer={<Button variant="secondary" onClick={() => setSelected(null)}>Close</Button>}>
        {selected && (
          <div>
            {selected.status === 'expired' && <Alert type="danger">This FSSAI license has expired. Restaurant should not be active.</Alert>}
            {isExpiringSoon(selected.expiryDate) && <Alert type="warning">License expiring within 30 days. Renewal required.</Alert>}
            {[
              ['License ID', selected.id], ['Restaurant', selected.restaurant],
              ['License Number', selected.licenseNumber], ['Type', selected.licenseType],
              ['Issued Date', formatDate(selected.issuedDate)], ['Expiry Date', formatDate(selected.expiryDate)],
              ['Status', selected.status], ['Verified By', selected.verifiedBy || 'Pending'],
            ].map(([l, v]) => (
              <div key={l} className="admin-info-row">
                <span className="admin-info-row-label">{l}</span>
                <span className="admin-info-row-value">{v}</span>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default FoodCompliance;
